import { Link } from "react-router-dom";
import { Compass, BadgePercent, Sparkles, FileCheck2, ArrowRight } from "lucide-react";
import { IMG } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { Reveal, Stagger, Item } from "../components/shared";
import { useI18n } from "../i18n/LanguageContext";

const ICONS = [Compass, BadgePercent, Sparkles, FileCheck2];

export default function AboutPage() {
  const { t, c } = useI18n();
  const a = t.about;
  return (
    <div data-testid="about-page">
      <Seo path="/about" title={t.seo.about.title} description={t.seo.about.desc} image={IMG.skyline}
        jsonLd={breadcrumbJsonLd([{ name: t.crumbHome, path: "/" }, { name: a.crumb, path: "/about" }])} />
      <PageHero eyebrow={a.eyebrow} crumb={a.crumb} title={a.title} sub={a.sub} image={IMG.skyline} />

      <section className="py-16 sm:py-24">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal><div className="overflow-hidden rounded-[2rem]"><img src={IMG.skyline} alt="Dubai skyline" className="h-[360px] w-full object-cover sm:h-[460px]" /></div></Reveal>
          <div>
            <span className="eyebrow">{a.mvEyebrow}</span>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">{a.mvTitle}</h2></Reveal>
            <Reveal delay={0.12}><p className="mt-5 text-base leading-relaxed text-muted">{a.mv1}</p></Reveal>
            <Reveal delay={0.18}><p className="mt-4 text-base leading-relaxed text-muted">{a.mv2}</p></Reveal>
            <Reveal delay={0.24}><Link to="/contact" className="btn-primary mt-8">{a.talk} <ArrowRight size={16} /></Link></Reveal>
          </div>
        </div>
      </section>

      <section className="pb-8">
        <div className="container-x">
          <div className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-gold to-gold-400 px-6 py-12 sm:px-12">
            <Stagger className="grid grid-cols-2 gap-8 lg:grid-cols-4">
              {c.STATS.map((s, i) => (
                <Item key={i} className="text-center"><p className="font-display text-4xl font-semibold text-white sm:text-5xl">{s.value}</p><p className="mt-2 text-sm font-medium text-white/85">{s.label}</p></Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="eyebrow">{a.valuesEyebrow}</span>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">{a.valuesTitle}</h2></Reveal>
          </div>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {c.MANIFESTO.map((m, i) => {
              const Icon = ICONS[i];
              return (
                <Item key={m.no}>
                  <div className="group h-full rounded-3xl border border-navy/5 bg-white p-7 shadow-soft transition-transform duration-500 hover:-translate-y-1.5">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold-300 transition-colors duration-500 group-hover:bg-gold group-hover:text-white"><Icon size={24} /></span>
                    <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-navy">{m.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted">{m.body}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="eyebrow">{a.howEyebrow}</span>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">{a.howTitle}</h2></Reveal>
          </div>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {c.PROCESS.map((p) => (
              <Item key={p.no}>
                <div className="relative h-full rounded-3xl bg-cream p-7">
                  <span className="font-display text-5xl font-semibold text-gold/25">{p.no}</span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{p.body}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
